import { Form, InputNumber, Radio } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ANY_COACH, ANY_HUG } from '@salaries/shared';
import {
  deleteHugimAddonPercent,
  getHugimAddonSettings,
  upsertHugimAddonPercent,
} from '../../api/hugim';
import { byCourse, CoursePercentList, type CourseRolePercent } from './CoursePercentList';
import { PercentSumWarning } from './PercentSumWarning';

// מוצרים נלווים (add-ons) on a hugim box — the "Add-ons" column of the course
// revenue report (a show ticket, a kit…), which Arbox keeps OUT of the course
// payment, so the course percentage never covered it.
//
// Same shape as "אחוז מהתשלום לחוגים": a default percentage per role (primary /
// secondary staff member — fields of the parent form, saved with the drawer,
// blank = none) and a per-course list that saves immediately. Plus when the
// payout lands, one choice for the whole box. The per-staff and
// per-staff+course levels live in the staff member's salary drawer.
export function AddonsFields() {
  const form = Form.useFormInstance();
  const primaryDefault = Form.useWatch('addons_default_percent', form);
  const secondaryDefault = Form.useWatch('addons_secondary_default_percent', form);
  const qc = useQueryClient();
  const settingsQ = useQuery({ queryKey: ['hugim-addon-percentages'], queryFn: getHugimAddonSettings });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['hugim-addon-percentages'] });
    void qc.invalidateQueries({ queryKey: ['payroll-report'] });
  };
  const save = useMutation({
    mutationFn: async (e: CourseRolePercent) => {
      const base = { coach_arbox_users_boxes_id: ANY_COACH, hug_key: e.hug_key };
      if (e.primary != null) await upsertHugimAddonPercent({ ...base, percent: e.primary, role: 'primary' });
      if (e.secondary != null)
        await upsertHugimAddonPercent({ ...base, percent: e.secondary, role: 'secondary' });
    },
    onSuccess: invalidate,
    onError: (e) => toast.error((e as Error).message || 'שגיאה בשמירה'),
  });
  const remove = useMutation({
    mutationFn: async (e: CourseRolePercent) => {
      if (e.primary != null) await deleteHugimAddonPercent(ANY_COACH, e.hug_key, 'primary');
      if (e.secondary != null) await deleteHugimAddonPercent(ANY_COACH, e.hug_key, 'secondary');
    },
    onSuccess: invalidate,
    onError: (e) => toast.error((e as Error).message || 'שגיאה במחיקה'),
  });
  const courseRows = byCourse(
    (settingsQ.data?.overrides ?? []).filter(
      (o) => o.coach_arbox_users_boxes_id === ANY_COACH && o.hug_key !== ANY_HUG,
    ),
  );

  return (
    <>
      <Form.Item
        name="addons_default_percent"
        label="אחוז ברירת מחדל לאיש צוות ראשי ממוצרים נלווים (%)"
        className="mb-2"
        normalize={(v) => (v === '' || v == null ? null : v)}
      >
        <InputNumber min={0} max={100} style={{ width: 160 }} dir="ltr" />
      </Form.Item>
      <Form.Item
        name="addons_secondary_default_percent"
        label="אחוז ברירת מחדל לאיש צוות משני ממוצרים נלווים (%)"
        className="mb-0"
        normalize={(v) => (v === '' || v == null ? null : v)}
      >
        <InputNumber min={0} max={100} style={{ width: 160 }} dir="ltr" />
      </Form.Item>
      <PercentSumWarning primary={primaryDefault} secondary={secondaryDefault} />
      <div className="text-small text-secondary-text mt-2 mb-3">
        חל על מוצרים נלווים בכל החוגים. אחוז לפי חוג גובר עליו.
      </div>

      <div className="mb-4">
        <CoursePercentList
          rows={courseRows}
          adding={save.isPending}
          onAdd={(hug_key, primary, secondary, done) =>
            save.mutate({ hug_key, primary, secondary }, { onSuccess: done })
          }
          onRemove={(hug) => {
            const e = courseRows.find((o) => o.hug_key === hug);
            if (e) remove.mutate(e);
          }}
        />
      </div>

      <Form.Item name="addons_payment_method" label="אופן התשלום" className="mb-0">
        <Radio.Group className="flex flex-col gap-3">
          <Radio value="spread">פריסה לפי חודשי הקורס</Radio>
          <Radio value="next_month">תשלום בחודש העוקב</Radio>
        </Radio.Group>
      </Form.Item>
    </>
  );
}
